import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { headers } from '../../styles/type';
import { containerStyles } from '../../styles/layout';
import { shadow } from '../../styles/box';

import Button from '../buttons/Button';
import ButtonGroup from '../ButtonGroup';
import ButtonText from '../buttons/ButtonText';
import Image from '../Image';
import TextRenderer from '../TextRenderer';
import Background from '../Background';
import { StyledSplitHero } from './SplitHero';

const StyledBackground = styled(Background)`
  padding: ${rem(32)} 0;

  ${media('>tiny')} {
    padding: ${rem(48)} 0;
  }

  ${StyledSplitHero} + & {
    margin-top: ${rem(-32)};

    ${media('>tiny')} {
      margin-top: ${rem(-48)};
    }
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  ${containerStyles.small}
`;

const ContentContainer = styled.div`
  ${shadow}
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: ${rem(16)};
  border-radius: ${rem(10)};
  background: ${colors.white};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
    border-radius: 10px;
    border: 1px solid ${colors.blue['700']};
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);
  `}

  ${media('>tiny')} {
    padding: ${rem(16)} ${rem(20)};
  }

  ${media('>smedium')} {
    padding: ${rem(24)} ${rem(32)};
  }

  ${ButtonGroup} {
    padding: ${rem(12)};
  }
`;

const StyledImage = styled(Image)`
  margin-right: ${rem(24)};
`;

const Content = styled.div`
  flex: 1 1 auto;
  max-width: ${rem(480)};
  margin-right: auto;
  padding: ${rem(12)};
`;

const Subhead = styled.p`
  ${headers.eyebrow}
  margin-bottom: ${rem(4)};
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['200']};
  `}
`;

const Headline = styled.h2`
  ${headers.h4}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const MiniCta = ({
  image,
  subhead,
  headline,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  backgroundColor = 'White',
  backgroundPattern
}) => (
  <StyledBackground
    color={backgroundColor}
    pattern={backgroundPattern}
    patternVariation={backgroundPattern === 'Large Dots' ? 'short' : null}
  >
    <ContentWrapper>
      <ContentContainer>
        {image && <StyledImage image={image} objectFit="contain" />}
        <Content>
          {subhead && (
            <Subhead>
              <TextRenderer text={subhead} />
            </Subhead>
          )}
          {headline && (
            <Headline>
              <TextRenderer text={headline} />
            </Headline>
          )}
        </Content>
        <ButtonGroup>
          {primaryCtaText && primaryCtaLink && (
            <Button
              to={primaryCtaLink}
              aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
            >
              <TextRenderer text={primaryCtaText} />
            </Button>
          )}
          {secondaryCtaText && secondaryCtaLink && (
            <ButtonText
              to={secondaryCtaLink}
              aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
            >
              <TextRenderer text={secondaryCtaText} />
            </ButtonText>
          )}
        </ButtonGroup>
      </ContentContainer>
    </ContentWrapper>
  </StyledBackground>
);

MiniCta.propTypes = {
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object,
    description: PropTypes.string
  }),
  subhead: PropTypes.string,
  headline: PropTypes.string,
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Large Dots', 'Splash Dots'])
};

export const query = graphql`
  fragment MiniCtaFragment on ContentfulMiniCta {
    id
    __typename
    image {
      gatsbyImageData(width: 64, height: 64, quality: 75)
      file {
        url
      }
      description
    }
    subhead
    headline
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    backgroundColor
    backgroundPattern
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default MiniCta;
